import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Copy, Gamepad2, Cpu, Loader2, Zap, LogOut, Download, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, BRAND } from "@/lib/constants";
import { fetchPdfObjectUrl } from "@/lib/pdf";
import { useSeo } from "@/lib/seo";

// Tweaks-only for now — cash rewards exist on the backend (a referrer's reward_type can already
// be "cash"), but the Stripe Connect onboarding + payout cron that would let anyone actually cash
// out don't exist yet. Signup here is locked to reward_type: "tweaks" until that ships.
const POINT_COSTS = { console: 1, pc: 3 };
const PLAN_META = {
  console: { label: "Free Console Tweak", icon: Gamepad2 },
  pc: { label: "Free PC Tweak", icon: Cpu },
};

const TOKEN_KEY = "ft_referral_token";

function copyToClipboard(text, message) {
  navigator.clipboard.writeText(text);
  toast.success(message);
}

function StatBlock({ label, value }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-4">
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1">{label}</p>
      <p className="text-2xl font-black tabular-nums" style={{ fontFamily: "'Orbitron', sans-serif" }}>
        {value}
      </p>
    </div>
  );
}

function RedeemCard({ planId, pointsBalance, onRedeem, redeeming }) {
  const meta = PLAN_META[planId];
  const Icon = meta.icon;
  const cost = POINT_COSTS[planId];
  const affordable = pointsBalance >= cost;

  return (
    <div
      className={`border p-6 flex flex-col ${
        affordable ? "border-orange-500/40 bg-orange-500/5" : "border-white/10 bg-[#0a0a0a]"
      }`}
      data-testid={`redeem-card-${planId}`}
    >
      <Icon className={`w-6 h-6 mb-3 ${affordable ? "text-orange-500" : "text-zinc-600"}`} />
      <p className="font-black uppercase tracking-tight text-lg mb-1" style={{ fontFamily: "'Orbitron', sans-serif" }}>
        {meta.label}
      </p>
      <p className="text-zinc-500 text-xs uppercase tracking-widest mb-4">
        {cost} point{cost > 1 ? "s" : ""}
      </p>
      <button
        type="button"
        disabled={!affordable || redeeming}
        onClick={() => onRedeem(planId)}
        className="mt-auto w-full bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-black font-black uppercase tracking-[0.2em] text-xs py-3 transition-colors flex items-center justify-center gap-2"
        data-testid={`redeem-btn-${planId}`}
      >
        {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {affordable ? "Redeem" : `Need ${cost - pointsBalance} more`}
      </button>
    </div>
  );
}

function ConsoleGuideModal({ onCancel, onConfirm, busy }) {
  const [agreed, setAgreed] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-orange-500/30 p-6 w-full max-w-md">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-black uppercase tracking-tighter" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Console Tweak Guide
          </h3>
          <button onClick={onCancel} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-zinc-500 text-xs mb-5">
          Console has no app to unlock — you'll get a PDF guide with the exact settings to change
          yourself, on your own console.
        </p>

        <label className="flex items-start gap-3 cursor-pointer mb-6">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 accent-orange-500 w-4 h-4 shrink-0"
          />
          <span className="text-zinc-300 text-sm leading-relaxed">
            I agree to the{" "}
            <Link to="/terms" target="_blank" className="text-orange-500 underline">
              Terms &amp; Conditions
            </Link>{" "}
            and won't redistribute, resell, or publicly repost this guide.
          </span>
        </label>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 border border-white/10 text-zinc-400 text-xs uppercase tracking-[0.2em] font-bold hover:border-white/30"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!agreed || busy}
            className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-400 text-black text-xs uppercase tracking-[0.2em] font-black disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Agree &amp; Get Guide
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Referral() {
  useSeo({
    title: "Referral Program — Flow Tweaks",
    description:
      "Refer a player, earn free tweaks or cash. One referral is a free console tweak — no cap, no catch.",
    path: "/referral",
  });

  // "email" stage: enter email (+ discord handle, only used if this turns out to be a new
  // signup) and request a login code. "code" stage: enter the 6-digit code just emailed.
  // "dashboard": verified, token stored, balance loaded.
  const [stage, setStage] = useState("restoring");
  const [email, setEmail] = useState("");
  const [discordHandle, setDiscordHandle] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState(null);
  const [redeemingPlan, setRedeemingPlan] = useState(null);
  const [showConsoleTos, setShowConsoleTos] = useState(false);
  const [guideUrl, setGuideUrl] = useState(null);
  const [guideFetchFailed, setGuideFetchFailed] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchBalance = async () => {
    const res = await axios.get(`${API}/referral/balance`, { headers: authHeaders() });
    setAccount(res.data);
    setStage("dashboard");
  };

  // On load, a stored token is only ever a starting point to re-verify — never trusted blind.
  // A missing/expired/invalid token just falls back to the email step, same as a first visit.
  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setStage("email");
      return;
    }
    fetchBalance().catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setStage("email");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/referral/auth/request`, {
        email,
        ...(discordHandle ? { discord_handle: discordHandle } : {}),
      });
      toast.success("Code sent — check your inbox");
      setStage("code");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send a code — try again");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/referral/auth/verify`, { email, code });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      await fetchBalance();
      toast.success("You're in");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired code");
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAccount(null);
    setEmail("");
    setDiscordHandle("");
    setCode("");
    setStage("email");
  };

  // Console never unlocks the app — there's nothing to "open the app and sign in" for. It needs
  // a TOS/no-distribution agreement first, then a self-serve PDF guide instead. PC still unlocks
  // instantly in the app, no extra step.
  const redeem = (planId) => {
    if (planId === "console") {
      setShowConsoleTos(true);
      return;
    }
    redeemDirect(planId);
  };

  const redeemDirect = async (planId) => {
    setRedeemingPlan(planId);
    try {
      await axios.post(`${API}/referral/redeem`, { plan_id: planId }, { headers: authHeaders() });
      toast.success(`${PLAN_META[planId].label} unlocked! Open the Flow Tweaks app and sign in with this email.`);
      await fetchBalance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Redemption failed");
    } finally {
      setRedeemingPlan(null);
    }
  };

  const confirmConsoleRedeem = async () => {
    setRedeemingPlan("console");
    setGuideFetchFailed(false);
    try {
      await axios.post(`${API}/referral/redeem`, { plan_id: "console" }, { headers: authHeaders() });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Redemption failed");
      setRedeemingPlan(null);
      return;
    }
    setShowConsoleTos(false);
    await fetchBalance();
    // Redeem already succeeded and spent the point — a failure past this point just means the
    // guide itself couldn't be fetched, not that the redemption needs retrying.
    try {
      const url = await fetchPdfObjectUrl(`${API}/referral/console-guide`, { agree: true }, authHeaders());
      setGuideUrl(url);
      toast.success("Console tweak redeemed — your guide is ready below.");
    } catch (err) {
      setGuideFetchFailed(true);
      toast.error("Redeemed! Couldn't fetch your guide though — tap Retry below.");
    } finally {
      setRedeemingPlan(null);
    }
  };

  const retryGuideFetch = async () => {
    setBusy(true);
    try {
      const url = await fetchPdfObjectUrl(`${API}/referral/console-guide`, { agree: true }, authHeaders());
      setGuideUrl(url);
      setGuideFetchFailed(false);
    } catch (err) {
      toast.error(err.message || "Couldn't fetch your guide — try again");
    } finally {
      setBusy(false);
    }
  };

  const shareLink = account ? `${window.location.origin}/?ref=${account.code}` : "";

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="referral-page">
      {showConsoleTos && (
        <ConsoleGuideModal
          onCancel={() => setShowConsoleTos(false)}
          onConfirm={confirmConsoleRedeem}
          busy={redeemingPlan === "console"}
        />
      )}

      <header className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
            <span className="font-black tracking-[0.18em] text-sm uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              {BRAND.name}
            </span>
          </Link>
          <div className="flex items-center gap-5">
            {stage === "dashboard" && (
              <button
                onClick={signOut}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            )}
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to site
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <p className="text-orange-500 font-bold tracking-[0.3em] text-xs uppercase mb-4">Referral Program</p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-6 leading-[0.95]"
          style={{ fontFamily: "'Orbitron', sans-serif" }}
        >
          Send a Friend,
          <br />
          Get a Free Tweak
        </h1>
        <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-10 max-w-2xl">
          Get your own code, share it, and earn 1 point every time someone buys using it — no
          matter which plan they pick. Trade points in for free tweaks, unlocked instantly in the
          desktop app. Cash payouts to your bank are coming soon.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-16 max-w-xl">
          <div className="border border-white/10 bg-[#0a0a0a] p-5">
            <p className="text-2xl font-black" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              1 point
            </p>
            <p className="text-zinc-500 text-xs uppercase tracking-widest mt-1">Free Console Tweak</p>
          </div>
          <div className="border border-white/10 bg-[#0a0a0a] p-5">
            <p className="text-2xl font-black" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              3 points
            </p>
            <p className="text-zinc-500 text-xs uppercase tracking-widest mt-1">Free PC Tweak</p>
          </div>
        </div>

        {stage === "restoring" && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your session…
          </div>
        )}

        {stage === "email" && (
          <div className="max-w-md border border-white/10 bg-[#0a0a0a] p-8" data-testid="referral-lookup">
            <h2 className="text-xl font-black uppercase tracking-tight mb-1" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              Get Started
            </h2>
            <p className="text-zinc-500 text-sm mb-6">
              We&apos;ll email you a code — no password, ever. New here? Add your Discord handle
              too and we&apos;ll set your account up in the same step.
            </p>

            <form onSubmit={requestCode} className="space-y-4">
              <div>
                <Label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 bg-black border-white/10 rounded-none focus-visible:ring-orange-500"
                  data-testid="referral-email-input"
                  autoFocus
                  required
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">
                  Discord Handle <span className="text-zinc-600 normal-case">(new signups only)</span>
                </Label>
                <Input
                  value={discordHandle}
                  onChange={(e) => setDiscordHandle(e.target.value)}
                  placeholder="yourname"
                  className="mt-2 bg-black border-white/10 rounded-none focus-visible:ring-orange-500"
                  data-testid="referral-discord-input"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full bg-orange-500 hover:bg-orange-400 text-black font-black uppercase tracking-[0.2em] text-xs py-4 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="referral-submit-btn"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Send Me a Code
              </button>
            </form>
          </div>
        )}

        {stage === "code" && (
          <div className="max-w-md border border-white/10 bg-[#0a0a0a] p-8" data-testid="referral-code-entry">
            <h2 className="text-xl font-black uppercase tracking-tight mb-1" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              Check Your Email
            </h2>
            <p className="text-zinc-500 text-sm mb-6">
              Enter the 6-digit code we sent to {email}. It expires in 10 minutes.
            </p>

            <form onSubmit={verifyCode} className="space-y-4">
              <div>
                <Label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Code</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  className="mt-2 bg-black border-white/10 rounded-none focus-visible:ring-orange-500 tracking-[0.4em] text-center text-lg"
                  data-testid="referral-code-input"
                  autoFocus
                  required
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full bg-orange-500 hover:bg-orange-400 text-black font-black uppercase tracking-[0.2em] text-xs py-4 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="referral-verify-btn"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Verify
              </button>
              <button
                type="button"
                onClick={() => setStage("email")}
                className="w-full text-zinc-500 hover:text-zinc-300 text-xs uppercase tracking-widest"
              >
                Use a different email
              </button>
            </form>
          </div>
        )}

        {stage === "dashboard" && account && (
          <div className="space-y-10" data-testid="referral-dashboard">
            <div className="border border-orange-500/40 bg-orange-500/5 p-6">
              <p className="text-[10px] uppercase tracking-[0.3em] text-orange-500 font-bold mb-3">Your Code</p>
              <div className="flex flex-wrap items-center gap-4">
                <span
                  className="text-3xl sm:text-4xl font-black tabular-nums tracking-wider"
                  style={{ fontFamily: "'Orbitron', sans-serif" }}
                  data-testid="referral-code"
                >
                  {account.code}
                </span>
                <button
                  onClick={() => copyToClipboard(account.code, "Code copied")}
                  className="text-zinc-400 hover:text-orange-500 transition-colors inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold"
                >
                  <Copy className="w-4 h-4" /> Copy code
                </button>
                <button
                  onClick={() => copyToClipboard(shareLink, "Share link copied")}
                  className="text-zinc-400 hover:text-orange-500 transition-colors inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold"
                >
                  <Copy className="w-4 h-4" /> Copy share link
                </button>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-black uppercase tracking-tight mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                Your Balance
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatBlock label="Points" value={account.points_balance} />
                <StatBlock label="Pending Points" value={account.pending_points} />
                <StatBlock label="Referrals" value={account.referral_count} />
                <StatBlock label="Lifetime Points" value={account.lifetime_points} />
              </div>
              {account.pending_points > 0 && (
                <p className="text-zinc-500 text-xs mt-3">
                  Pending points become spendable after a 14-day hold, in case of a refund or dispute.
                </p>
              )}
            </div>

            <div>
              <h2 className="text-lg font-black uppercase tracking-tight mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                Redeem
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {Object.keys(PLAN_META).map((planId) => (
                  <RedeemCard
                    key={planId}
                    planId={planId}
                    pointsBalance={account.points_balance}
                    onRedeem={redeem}
                    redeeming={redeemingPlan === planId}
                  />
                ))}
              </div>
            </div>

            {guideUrl && (
              <div className="border border-orange-500/40 bg-orange-500/5 p-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-black uppercase tracking-tight text-lg" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                    Your Console Guide Is Ready
                  </p>
                  <p className="text-zinc-500 text-sm mt-1">Apply the settings yourself, on your own console.</p>
                </div>
                <a
                  href={guideUrl}
                  download="flow-tweaks-console-guide.pdf"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black font-black uppercase tracking-[0.2em] text-xs px-6 py-3 transition-colors"
                >
                  <Download className="w-4 h-4" /> Download PDF
                </a>
              </div>
            )}

            {guideFetchFailed && !guideUrl && (
              <div className="border border-red-500/40 bg-red-500/5 p-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-black uppercase tracking-tight text-lg text-red-400" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                    Console Tweak Redeemed
                  </p>
                  <p className="text-zinc-500 text-sm mt-1">Your point was spent, but the guide didn't load — try again.</p>
                </div>
                <button
                  onClick={retryGuideFetch}
                  disabled={busy}
                  className="inline-flex items-center gap-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 font-black uppercase tracking-[0.2em] text-xs px-6 py-3 transition-colors disabled:opacity-50"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  Retry
                </button>
              </div>
            )}

            <div className="border border-white/10 bg-[#0a0a0a] p-5">
              <p className="text-zinc-400 text-sm">
                Reward type: <span className="text-white font-bold uppercase">Tweaks</span> — free tweaks,
                unlocked instantly, no setup. Cash payouts to your bank are coming soon.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
